import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { Text } from "@/components/ui/baseUi/text"
import { BoxProgramColorSelection } from "@/modules/boxProgrammation/components/boxProgramColorSelection"
import { BoxProgramOfferForm } from "@/modules/boxProgrammation/components/boxProgramOfferForm"
import { getBoxProgramSchema, type BoxProgramFormFields } from "@/modules/boxProgrammation/formik/boxProgramFormTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { cn } from "@/modules/lib/utils"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"

export const BoxProgramInfoForm = withForm({
  defaultValues: {} as BoxProgramFormFields,
  props: {} as {
    isEditing: boolean
    className?: string
  } & React.ComponentProps<"div">,
  render: function BoxProgramInfoFormInner({ form, isEditing, className, ...props }) {
    const language = useLanguage()
    const boxProgramSchema = getBoxProgramSchema(isEditing, language)

    return (
      <div className={cn("flex flex-col gap-8", className)} {...props}>
        <form.AppField
          name="name"
          validators={{
            onChange: boxProgramSchema.shape.name,
            onBlur: boxProgramSchema.shape.name,
          }}
        >
          {(field) => (
            <field.Field>
              <field.Label required>
                {word("box.programmation.form.title.label")}              </field.Label>
              <field.Content>
                <field.Input placeholder={word("box.programmation.form.title.placeholder")} />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.AppField name="image">
          {(field) => (
            <field.Field>
              <field.Label className="uppercase">{word("program.form.image.label")}</field.Label>
              <field.Description className="flex items-center gap-2">
                <WarningHexagonalIcon
                  color="currentColor"
                  className="text-textDefault-caption"
                  width={24}
                  height={24}
                />
                {word("picture.warning.message")}
              </field.Description>
              <field.Content>
                <field.ImagePicker type="cover" placeHolder={{ message: word("program.form.uploadImage.text1") }} />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.AppField
          name="description"
          validators={{
            onChange: boxProgramSchema.shape.description,
            onBlur: boxProgramSchema.shape.description,
          }}
        >
          {(field) => (
            <field.Field>
              <field.Label required>
                {word("box.programmation.form.description.label")}              </field.Label>
              <field.Content>
                <field.Textarea
                  placeholder={word("box.programmation.form.description.placeholder")}
                  className="field-sizing-fixed"
                  rows={5}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.AppField name="color">
          {(field) => (
            <field.Field>
              <field.Label required>
                {word("box.programmation.form.label.color")}              </field.Label>
              <field.Content>
                <BoxProgramColorSelection
                  selectedColor={field.state.value}
                  onChangeSelectedColor={(color) => field.handleChange(color)}
                />
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <form.AppField name="defaultHideSessionContent">
          {(field) => (
            <field.Field>
              <field.Label required>
                {word("box.programmation.form.hideSessionContent.label")}              </field.Label>

              <field.Content>
                <field.SwitchInline textSwitch={word("box.programmation.form.hideSessionContent.textSwitch")} />
                <Text fontStyle="LM" className="px-4 text-textDefault-caption">
                  {word("box.programmation.form.hideSessionContent.description")}
                </Text>
                <field.Message />
              </field.Content>
            </field.Field>
          )}
        </form.AppField>

        <BoxProgramOfferForm form={form} />
      </div>
    )
  },
})
